import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { iconList } from 'site-modules/edmunds-design-system/components/shared/icon-list';
import { Button } from 'reactstrap';
import './eds-button.scss';

/**
 * Edmunds Design System (EDS) standard button.
 *
 * @param {object} props The component props.
 * @param {React.ReactNode} props.children The content to display inside the button (text, icons, etc.).
 * @param {string} [props.tag] The HTML tag or React component to render. Defaults to 'a' if `href` is present, otherwise 'button'.
 * @param {string} [props.href] The URL for the link. If provided, the component defaults to an `<a>` tag.
 * @param {string} [props.className] Additional CSS classes to apply to the button.
 * @param {boolean} [props.hideLabel] If true, the children will be visually hidden (useful for icon-only buttons).
 * @param {boolean} [props.isDisabled] If true, the button will be disabled.
 * @param {string} [props.iconName] The name of the icon to display.
 * @param {'left'|'right'} [props.iconPosition] The position of the icon relative to the text.
 * @param {'primary-1'|'secondary-1'|'primary-2'|'secondary-2'} [props.type] The button's style type.
 * @param {'small'|'medium'|'large'} [props.size] The size of the button.
 * @param {'squared'|'rounded'|'circular'} [props.style] The shape of the button.
 * @param {boolean} [props.isFluid] If true, the button will take up the full width of its container.
 * @param {Function} [props.onClick] A callback function to execute when the button is clicked.
 */
export function EDSButton({
  children,
  type,
  href,
  className,
  style,
  size,
  iconName,
  iconPosition,
  isFluid,
  hideLabel,
  isDisabled,
  onClick,
  tag,
  ...otherProps
}) {
  const Component = tag ?? (href ? 'a' : 'button');

  return (
    <Button
      {...otherProps}
      tag={Component}
      className={cn('eds-button', type, style, size, { fluid: isFluid }, className)}
      onClick={onClick}
      href={href}
      disabled={isDisabled}
      {...(Component !== 'button' && isDisabled ? { 'aria-disabled': true, 'data-disabled': true } : {})}
    >
      {iconPosition === 'left' && iconName && (
        <span className={cn('eds-icon', `icon-${iconName}`)} aria-hidden="true" />
      )}
      {!hideLabel && <span className="eds-button-text">{children}</span>}
      {iconPosition === 'right' && iconName && (
        <span className={cn('eds-icon', `icon-${iconName}`)} aria-hidden="true" />
      )}
    </Button>
  );
}

EDSButton.propTypes = {
  children: PropTypes.node.isRequired,
  tag: PropTypes.elementType,
  type: PropTypes.oneOf(['primary-1', 'secondary-1', 'primary-2', 'secondary-2']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  style: PropTypes.oneOf(['squared', 'rounded', 'circular']),
  iconName: PropTypes.oneOf(iconList),
  iconPosition: PropTypes.oneOf(['left', 'right']),
  isFluid: PropTypes.bool,
  isDisabled: PropTypes.bool,
  hideLabel: PropTypes.bool,
  href: PropTypes.string,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

EDSButton.defaultProps = {
  tag: undefined,
  type: 'primary-1',
  size: 'medium',
  style: 'squared',
  iconName: null,
  iconPosition: 'left',
  isFluid: false,
  isDisabled: false,
  hideLabel: false,
  onClick: undefined,
  href: undefined,
  className: '',
};
